import { DetailedInsight } from '@api/api/insights';
import { KgInsight } from '@api/api/kg-insights';
import { SentimentSummaryCard } from '@terminal/components/sentiment-summary-card/SentimentSummaryCard';
import { useInsightsById } from '@terminal/react-query-hooks/insights/useInsight';
import { useKgInsightsById } from '@terminal/react-query-hooks/insights/useKgInsight';
import { usePulseSentimentInsights } from '@terminal/react-query-hooks/themes/usePulseSentimentInsights';
import {
  POSTHOG_TRACKING_SOURCES,
  PosthogTrackingSource,
} from '@terminal/services/tracking/posthog/constants';
import { getMonthlySentimentState } from '@terminal/views/theme-overview/tab-content/pulse-overview/hooks/usePulseSummaries';
import { useSentimentScope } from '@terminal/views/theme-overview/tab-content/pulse-overview/hooks/useSentimentScope';
import {
  PulseMonthlySummaries,
  PulseMonthlySummary,
  PulseSentimentDirection,
  PulseSubject,
} from '@terminal/views/theme-overview/tab-content/pulse-overview/pulse-types';
import { citationIdsByTypeForDirection } from '@terminal/views/theme-overview/tab-content/pulse-overview/utils/citationIdsForDirection';
import { useEffect } from 'react';
import styles from './MonthlySentimentCards.module.scss';

const DIRECTIONS = ['tailwind', 'headwind'] as const;
type CardDirection = (typeof DIRECTIONS)[number];

const useMonthlySentimentState = ({
  summary,
  subject,
  direction,
}: {
  summary?: PulseMonthlySummary;
  subject: PulseSubject;
  direction: CardDirection;
}) => {
  const theme = subject.type === 'theme' ? subject.item : undefined;
  const sentimentScope = useSentimentScope(theme);
  const needsAllDrivers = !!summary && !summary.narrative.trim();
  const canLoadAllDrivers =
    needsAllDrivers && (!theme || sentimentScope.isReady);
  const allDrivers = usePulseSentimentInsights({
    subject,
    themeIds: theme ? sentimentScope.themeIds : undefined,
    companyIds: theme ? sentimentScope.companyIds : undefined,
    direction,
    enabled: canLoadAllDrivers,
  });
  const shouldFetchNextPage =
    canLoadAllDrivers &&
    !allDrivers.isLoading &&
    allDrivers.data.length === 0 &&
    allDrivers.hasNextPage &&
    !allDrivers.isFetchingNextPage &&
    !allDrivers.isError;

  useEffect(() => {
    if (shouldFetchNextPage) {
      void allDrivers.fetchNextPage();
    }
  }, [allDrivers.fetchNextPage, shouldFetchNextPage]);

  const isLoadingAllDrivers =
    needsAllDrivers &&
    (sentimentScope.isLoading ||
      (canLoadAllDrivers &&
        (allDrivers.isLoading ||
          allDrivers.isFetchingNextPage ||
          shouldFetchNextPage)));
  const hasAllDrivers =
    allDrivers.data.length > 0 || allDrivers.isError || sentimentScope.isError;

  return getMonthlySentimentState(summary, hasAllDrivers, isLoadingAllDrivers);
};

export const MonthlySentimentCards = ({
  summaries,
  subject,
  themeId,
  source = POSTHOG_TRACKING_SOURCES.THEME_OVERVIEW,
  getCardTitle,
  onOpenDrawer,
}: {
  summaries?: PulseMonthlySummaries;
  subject: PulseSubject;
  themeId?: string;
  source?: PosthogTrackingSource;
  getCardTitle?: (direction: PulseSentimentDirection) => string;
  onOpenDrawer: (direction: PulseSentimentDirection) => void;
}) => {
  const { kgInsightIds, scenarioInsightIds } = citationIdsByTypeForDirection(
    summaries,
    'all'
  );

  const kgInsightsById = useKgInsightsById(kgInsightIds);
  const scenarioInsightsById = useInsightsById(scenarioInsightIds);
  const insightsById = new Map<string, KgInsight | DetailedInsight>([
    ...kgInsightsById,
    ...scenarioInsightsById,
  ]);
  const scenarioInsightIdSet = new Set(scenarioInsightIds);
  const tailwindState = useMonthlySentimentState({
    summary: summaries?.tailwind,
    subject,
    direction: 'tailwind',
  });
  const headwindState = useMonthlySentimentState({
    summary: summaries?.headwind,
    subject,
    direction: 'headwind',
  });
  const states = { tailwind: tailwindState, headwind: headwindState };

  return (
    <div className={styles.root}>
      {DIRECTIONS.map(direction => {
        const summary = summaries?.[direction];

        return (
          <SentimentSummaryCard
            key={direction}
            direction={direction}
            title={getCardTitle?.(direction)}
            summary={summary}
            themeId={themeId}
            insightsById={insightsById}
            scenarioInsightIds={scenarioInsightIdSet}
            source={source}
            state={states[direction]}
            onOpen={() => onOpenDrawer(direction)}
          />
        );
      })}
    </div>
  );
};
